import * as React from "react";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import { toast } from "sonner";
import { List, ChevronDown, CircleInfoBold } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

export interface BulkActionsMenuItem<T extends string> {
  value: T;
  label: string;
}

export interface BulkActionsMenuProps<T extends string> {
  /** 트리거 버튼 문구 (예: "선택 충전기 일괄 변경") */
  triggerLabel: string;
  items: BulkActionsMenuItem<T>[];
  /** 선택된 행이 있는지. 없으면 항목이 비활성으로 보이고 안내 문구가 붙는다 */
  hasSelection: boolean;
  /** 선택이 없을 때 드롭다운 하단과 토스트에 함께 쓰는 안내 문구 */
  emptyMessage: string;
  onSelect: (value: T) => void;
}

/**
 * 목록 툴바의 "선택 N 일괄 변경" 드롭다운.
 * 선택된 행이 없으면 항목 클릭 시 토스트만 띄운다 (드롭다운 안내 문구와 동일)
 */
export function BulkActionsMenu<T extends string>({
  triggerLabel,
  items,
  hasSelection,
  emptyMessage,
  onSelect,
}: BulkActionsMenuProps<T>) {
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleSelect = (value: T) => {
    setMenuOpen(false);
    if (!hasSelection) {
      toast.error(emptyMessage);
      return;
    }
    onSelect(value);
  };

  return (
    <PopoverPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
      <PopoverPrimitive.Trigger asChild>
        <Button theme="assistive" variant="outline" size="sm">
          <List size={16} />
          {triggerLabel}
          <ChevronDown size={16} />
        </Button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          className={cn(
            "z-50 w-51 overflow-hidden rounded-xl bg-static-white",
            "inset-border-solid-normal shadow-[0px_4px_8px_0px_rgba(0,0,0,0.05)]",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          )}
          sideOffset={4}
          align="start"
        >
          <div
            className={cn(
              "flex flex-col gap-1 px-2 py-3",
              !hasSelection && "bg-fills-light inset-border-solid-normal-b",
            )}
          >
            {items.map((item) => (
              <button
                key={item.value}
                type="button"
                onClick={() => handleSelect(item.value)}
                className={cn(
                  "flex w-full items-center rounded-lg h-8 px-2 typo-label-1-semibold",
                  hasSelection
                    ? "text-labels-primary hover:bg-fills-primary"
                    : "text-labels-assistive",
                )}
              >
                {item.label}
              </button>
            ))}
          </div>
          {!hasSelection && (
            <div className="flex items-center gap-1 px-2 py-3 typo-caption-1-semibold text-labels-primary">
              <CircleInfoBold size={16} color="labels-alternative" />
              {emptyMessage}
            </div>
          )}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

BulkActionsMenu.displayName = "BulkActionsMenu";
